import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { CheckCircle2, Circle } from "lucide-react"
import Seo from "@/components/Seo"
import { api } from "@/lib/api"

const LABELS = { awaiting_payment: "Awaiting payment", processing: "Processing", dispatched: "Dispatched", delivered: "Delivered", ready_for_pickup: "Ready for Pickup", collected: "Collected" }
const input = "mt-1.5 w-full rounded-xl border border-brand-green/20 bg-brand-navy px-4 py-3 text-sm text-brand-light placeholder:text-brand-muted/60 focus:border-brand-green focus:outline-none"

function Timeline({ o }) {
  const at = o.flow.indexOf(o.status)
  const when = (s) => o.history.filter((h) => h.status === s).pop()
  return (
    <ol className="mt-6 space-y-4" data-testid="track-timeline">
      {o.flow.map((s, i) => {
        const done = at >= i
        const h = when(s)
        return (
          <li key={s} className="flex gap-3" data-testid={`track-step-${s}`}>
            {done ? <CheckCircle2 className="h-5 w-5 text-brand-green-light" /> : <Circle className="h-5 w-5 text-brand-muted/50" />}
            <div>
              <div className={`font-bold ${done ? "text-brand-light" : "text-brand-muted"}`}>{LABELS[s]}</div>
              {h && <div className="text-xs text-brand-muted">{new Date(h.ts).toLocaleString()}{h.note ? ` · ${h.note}` : ""}</div>}
            </div>
          </li>
        )
      })}
    </ol>
  )
}

export default function TrackOrder() {
  const [params] = useSearchParams()
  const [ref, setRef] = useState(params.get("ref") || "")
  const [email, setEmail] = useState("")
  const [order, setOrder] = useState(null)
  const [error, setError] = useState("")
  const [busy, setBusy] = useState(false)
  useEffect(() => setRef(params.get("ref") || ""), [params])

  const submit = async (e) => {
    e.preventDefault()
    setBusy(true); setError(""); setOrder(null)
    try { setOrder((await api.get("/orders/track", { params: { ref, email } })).data) }
    catch (err) { setError(err?.response?.data?.detail || "Could not look up your order.") }
    finally { setBusy(false) }
  }

  return (
    <section className="min-h-[70vh] bg-[#0A0F0B] px-6 py-20 md:px-14" data-testid="track-order-page">
      <Seo title="Track Your Order | Jero-One Technologies" description="Check the delivery status of your Jero-One Technologies order with your order reference and email." path="/track" />
      <div className="mx-auto max-w-2xl">
        <div className="font-mono text-xs tracking-widest text-brand-green-light">ORDER STATUS</div>
        <h1 className="mt-3 font-display text-6xl font-black leading-none text-brand-light">TRACK YOUR ORDER</h1>
        <p className="mt-4 text-brand-muted">Enter the order reference from your confirmation email (e.g. JO-1A2B3C4D) and the email you used at checkout.</p>
        <form onSubmit={submit} className="mt-8 grid gap-4 rounded-2xl border border-brand-green/15 bg-brand-mid p-6 sm:grid-cols-2">
          <label className="block text-xs text-brand-muted">Order reference
            <input value={ref} onChange={(e) => setRef(e.target.value)} required placeholder="JO-1A2B3C4D" data-testid="track-ref-input" className={input} /></label>
          <label className="block text-xs text-brand-muted">Email
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@company.com" data-testid="track-email-input" className={input} /></label>
          <button type="submit" disabled={busy} data-testid="track-submit-btn" className="rounded-xl bg-brand-green px-6 py-3.5 font-bold text-brand-light transition-colors hover:bg-brand-green-light disabled:opacity-60 sm:col-span-2">{busy ? "Checking…" : "Track order"}</button>
          {error && <p className="text-sm text-red-400 sm:col-span-2" data-testid="track-error">{error}</p>}
        </form>
        {order && (
          <div className="mt-8 rounded-2xl border border-brand-green/20 bg-brand-mid p-6" data-testid="track-result">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <h2 className="font-display text-3xl font-black text-brand-light">{order.ref}</h2>
              <span className="rounded-full bg-brand-green/20 px-3 py-1 text-xs font-bold text-brand-green-light" data-testid="track-status">{LABELS[order.status]}</span>
            </div>
            <p className="mt-2 text-sm text-brand-muted">{order.delivery.label || "Delivery"}{order.delivery.city ? ` · ${order.delivery.city}` : ""} · Placed {new Date(order.placed).toLocaleDateString()}</p>
            {(order.courier || order.tracking) && <p className="mt-2 text-sm text-brand-light" data-testid="track-courier">Courier: {order.courier || "-"} · Tracking no.: {order.tracking || "-"}</p>}
            {order.paid ? <Timeline o={order} /> : <p className="mt-4 text-sm text-amber-200">We haven't received payment for this order yet.</p>}
            <ul className="mt-6 border-t border-brand-green/10 pt-4 text-sm text-brand-muted">{order.items.map((i) => <li key={i}>{i}</li>)}</ul>
          </div>
        )}
      </div>
    </section>
  )
}
